// src/pages/SettingsPage.tsx — the pricing formula's global inputs.
import { useCallback, useEffect, useState } from "react";
import { ops, type Change, type Settings } from "../lib/ops";
import { dateTime, parseDollars, toDollarText } from "../lib/format";
import { useServices } from "../lib/context";
import { ChangePanel, type Pending } from "../components/ChangePanel";
import { Field, Loading, Notice, PageFrame, SectionLabel } from "../components/ui";

export default function SettingsPage() {
  const services = useServices();
  const [s, setS] = useState<Settings | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [pending, setPending] = useState<Pending | null>(null);

  const load = useCallback(async () => {
    try {
      setS(await ops.getSettings());
      setError(null);
    } catch (e) {
      setError((e as Error).message);
    }
  }, []);
  useEffect(() => {
    void load();
  }, [load]);

  const panel = pending ? (
    <ChangePanel
      pending={pending}
      onClose={() => setPending(null)}
      onApplied={async () => {
        await Promise.all([load(), services.refresh()]);
      }}
    />
  ) : undefined;

  return (
    <PageFrame
      title="Settings"
      lede="The inputs every price and margin is computed from. A change here previews every active service."
      panel={panel}
    >
      {!s && !error && <Loading what="settings" />}
      {error && <Notice kind="error">{error}</Notice>}
      {s && <SettingsForm key={JSON.stringify(s)} s={s} onPropose={setPending} />}
    </PageFrame>
  );
}

const csv = (a: (string | number)[]) => a.join(", ");
const splitCsv = (t: string) => t.split(",").map((x) => x.trim()).filter(Boolean);

function SettingsForm({ s, onPropose }: { s: Settings; onPropose: (p: Pending) => void }) {
  const [feePct, setFeePct] = useState(String(s.card_fee_pct));
  const [feeFixed, setFeeFixed] = useState(toDollarText(s.card_fee_fixed_cents));
  const [zips, setZips] = useState(csv(s.reference_zips));
  const [target, setTarget] = useState(String(s.target_margin_pct));
  const [floor, setFloor] = useState(String(s.margin_floor_pct));
  const [ra, setRa] = useState(toDollarText(s.rate_titulo_aprendiz_cents));
  const [rm, setRm] = useState(toDollarText(s.rate_titulo_oficial_cents));
  const [rd, setRd] = useState(toDollarText(s.rate_titulo_maestro_cents));
  const [mob, setMob] = useState(toDollarText(s.mobilization_cents));
  const [endings, setEndings] = useState(csv(s.price_endings));
  // SR-B2: Lit Up (priced by track)
  const [ladder, setLadder] = useState(toDollarText(s.ladder_premium_cents_per_hr ?? 0));
  const [ladder3, setLadder3] = useState(toDollarText(s.ladder_premium_3_cents_per_hr ?? 1500));   // GM-PRICE-02
  const [visitMin, setVisitMin] = useState(toDollarText(s.cart_visit_minimum_cents ?? null));
  // HSC-01: single-booking minimum (one service, one payment)
  const [singleOn, setSingleOn] = useState(s.single_visit_minimum_enabled ?? true);
  const [singleMin, setSingleMin] = useState(toDollarText(s.single_visit_minimum_cents ?? 25000));

  const errors: string[] = [];
  const num = (t: string, lo: number, hi: number, label: string) => {
    const n = Number(t);
    if (t.trim() === "" || !Number.isFinite(n) || n < lo || n > hi) errors.push(`${label}: ${lo}–${hi}`);
    return n;
  };
  const cents = (t: string, lo: number, hi: number, label: string) => {
    const c = parseDollars(t);
    if (c === null || c < lo || c > hi) errors.push(`${label}: ${toDollarText(lo)}–${toDollarText(hi)} dollars`);
    return c ?? 0;
  };

  const next = {
    card_fee_pct: num(feePct, 0, 10, "Card fee %"),
    card_fee_fixed_cents: cents(feeFixed, 0, 200, "Card fee fixed"),
    target_margin_pct: num(target, 0, 90, "Target margin"),
    margin_floor_pct: num(floor, 0, 90, "Margin floor"),
    rate_titulo_aprendiz_cents: cents(ra, 1000, 20000, "Aprendiz rate"),
    rate_titulo_oficial_cents: cents(rm, 1000, 20000, "Oficial rate"),
    rate_titulo_maestro_cents: cents(rd, 1000, 20000, "Maestro rate"),
    mobilization_cents: cents(mob, 0, 20000, "Trip cost"),
  };
  const zipList = splitCsv(zips);
  if (zipList.length < 1 || zipList.length > 5 || zipList.some((z) => !/^\d{5}$/.test(z))) errors.push("Reference ZIPs: 1–5 five-digit ZIPs");
  const endList = splitCsv(endings).map(Number);
  if (endList.length < 1 || endList.length > 10 || endList.some((e) => !Number.isInteger(e) || e < 0 || e > 99)) {
    errors.push("Price endings: 1–10 whole numbers 0–99");
  }

  // SR-B2: ladder premium 0–50 $/h; visit minimum blank (none) or $1–$10,000
  const ladderC = cents(ladder, 0, 5000, "Ladder premium, 2 stories");
  const ladder3C = cents(ladder3, 0, 10000, "Ladder premium, 3+ stories");   // GM-PRICE-02
  if (ladder3C !== null && ladderC !== null && ladder3C < ladderC) errors.push("Ladder premium: 3+ stories can't be less than 2 stories");
  const visitMinC = visitMin.trim() === "" ? null : parseDollars(visitMin);
  if (visitMin.trim() !== "" && (visitMinC === null || visitMinC < 100 || visitMinC > 1000000)) {
    errors.push("Visit minimum: blank for none, or 1–10000 dollars");
  }

  // HSC-01: single-booking minimum $0–$10,000
  const singleMinC = cents(singleMin, 0, 1000000, "Single-booking minimum");

  const change: Change = { type: "settings" };
  if (errors.length === 0) {
    if (ladderC !== (s.ladder_premium_cents_per_hr ?? 0)) change.ladder_premium_cents_per_hr = ladderC;
    if (ladder3C !== (s.ladder_premium_3_cents_per_hr ?? 1500)) change.ladder_premium_3_cents_per_hr = ladder3C;
    if (visitMinC !== (s.cart_visit_minimum_cents ?? null)) change.cart_visit_minimum_cents = visitMinC;
    if (singleOn !== (s.single_visit_minimum_enabled ?? true)) change.single_visit_minimum_enabled = singleOn;
    if (singleMinC !== (s.single_visit_minimum_cents ?? 25000)) change.single_visit_minimum_cents = singleMinC;
    for (const [k, v] of Object.entries(next) as [keyof typeof next, number][]) {
      if (v !== Number(s[k])) change[k] = v;
    }
    if (csv(zipList) !== csv(s.reference_zips)) change.reference_zips = zipList;
    if (csv(endList) !== csv(s.price_endings)) change.price_endings = endList;
  }
  const dirty = Object.keys(change).length > 1;

  return (
    <div className="settings">
      <section className="editor-section card">
        <SectionLabel>Card fees</SectionLabel>
        <div className="fields">
          <Field label="Percent" htmlFor="fee-pct">
            <input id="fee-pct" inputMode="decimal" value={feePct} onChange={(e) => setFeePct(e.target.value)} />
          </Field>
          <Field label="Fixed per charge ($)" htmlFor="fee-fixed">
            <input id="fee-fixed" inputMode="decimal" value={feeFixed} onChange={(e) => setFeeFixed(e.target.value)} />
          </Field>
        </div>
      </section>

      <section className="editor-section card">
        <SectionLabel>Margins</SectionLabel>
        <div className="fields">
          <Field label="Target margin (%)" htmlFor="target" hint="Margins above this show in sage.">
            <input id="target" inputMode="decimal" value={target} onChange={(e) => setTarget(e.target.value)} />
          </Field>
          <Field label="Margin floor (%)" htmlFor="floor" hint="Margins below this show in rust.">
            <input id="floor" inputMode="decimal" value={floor} onChange={(e) => setFloor(e.target.value)} />
          </Field>
        </div>
      </section>

      <section className="editor-section card">
        <SectionLabel>Título rates and trip cost</SectionLabel>
        <div className="fields">
          <Field label="Aprendiz ($/h)" htmlFor="ra">
            <input id="ra" inputMode="decimal" value={ra} onChange={(e) => setRa(e.target.value)} />
          </Field>
          <Field label="Oficial ($/h)" htmlFor="rm" hint="Customer prices are set at this rate.">
            <input id="rm" inputMode="decimal" value={rm} onChange={(e) => setRm(e.target.value)} />
          </Field>
          <Field label="Maestro ($/h)" htmlFor="rd">
            <input id="rd" inputMode="decimal" value={rd} onChange={(e) => setRd(e.target.value)} />
          </Field>
          <Field label="Trip cost per visit ($)" htmlFor="mob" hint="Paid to the Maestro once per visit.">
            <input id="mob" inputMode="decimal" value={mob} onChange={(e) => setMob(e.target.value)} />
          </Field>
        </div>
      </section>

      <section className="editor-section card">
        <SectionLabel>Visit minimum (single bookings)</SectionLabel>
        <div className="fields">
          <Field label="Enforce the minimum" htmlFor="single-on" hint="When on, a single booking below the minimum is refused at checkout. Services marked 'Allowed below the minimum' are exempt.">
            <label className="check">
              <input id="single-on" type="checkbox" checked={singleOn} onChange={(e) => setSingleOn(e.target.checked)} /> {singleOn ? "On" : "Off"}
            </label>
          </Field>
          <Field label="Minimum ($)" htmlFor="single-min" hint="Applies to one-service bookings. The cart has its own minimum below.">
            <input id="single-min" inputMode="decimal" value={singleMin} onChange={(e) => setSingleMin(e.target.value)} disabled={!singleOn} />
          </Field>
        </div>
      </section>

      <section className="editor-section card">
        <SectionLabel>Ladder premium by house stories</SectionLabel>
        <div className="fields">
          <Field label="2 stories ($ per job hour)" htmlFor="ladder" hint="Added to the Maestro's tarifa on 2-story houses (Storm Ready, house lights). Tree wraps always get it.">
            <input id="ladder" inputMode="decimal" value={ladder} onChange={(e) => setLadder(e.target.value)} />
          </Field>
          <Field label="3+ stories ($ per job hour)" htmlFor="ladder3" hint="Added to the tarifa on 3-story houses. The Property Owner's price adds the premium plus the target margin (house lights use their story step).">
            <input id="ladder3" inputMode="decimal" value={ladder3} onChange={(e) => setLadder3(e.target.value)} />
          </Field>
        </div>
      </section>

      <section className="editor-section card">
        <SectionLabel>Christmas lights (priced by track)</SectionLabel>
        <div className="fields">
          <Field label="Cart minimum ($)" htmlFor="visit-min" hint="Smallest cart order (Christmas lights) customers can book. Blank for no minimum.">
            <input id="visit-min" inputMode="decimal" value={visitMin} onChange={(e) => setVisitMin(e.target.value)} placeholder="No minimum" />
          </Field>
        </div>
      </section>

      <section className="editor-section card">
        <SectionLabel>Reference ZIPs and rounding</SectionLabel>
        <div className="fields">
          <Field label="Reference ZIPs" htmlFor="zips" hint="Comma-separated. Previews and the Services list price these.">
            <input id="zips" value={zips} onChange={(e) => setZips(e.target.value)} />
          </Field>
          <Field label="Price endings" htmlFor="endings" hint="Formula prices round to the nearest ending, e.g. 49, 79, 99.">
            <input id="endings" value={endings} onChange={(e) => setEndings(e.target.value)} />
          </Field>
        </div>
      </section>

      {errors.length > 0 && <p className="hint warn">{errors.join(". ")}.</p>}
      {s.updated_at && (
        <p className="hint">
          Last changed {dateTime(s.updated_at)}
          {s.updated_by_email ? ` by ${s.updated_by_email}` : ""}.
        </p>
      )}
      <button
        type="button"
        className="btn primary"
        disabled={!dirty || errors.length > 0}
        onClick={() => onPropose({ change, title: "Pricing settings" })}
      >
        Preview settings change
      </button>
    </div>
  );
}
